import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Loader2, MailCheck, Mail, Phone, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import PageLayout from "@/components/layout/PageLayout";
import { supabase } from "@/integrations/supabase/client";
const formSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters."
  }),
  email: z.string().email({
    message: "Please enter a valid email address."
  }),
  company: z.string().min(1, {
    message: "Company name is required."
  }),
  message: z.string().optional()
});
const RequestDemo = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const {
    toast
  } = useToast();
  const {
    t
  } = useTranslation("requestDemo");
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      message: ""
    }
  });
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    console.log("Submitting form with values:", values);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('send-contact-email', {
        body: {
          type: 'contact',
          name: values.name,
          email: values.email,
          company: values.company,
          message: values.message || 'No message provided'
        }
      });
      console.log("Response from send-contact-email function:", data, error);
      if (error) throw error;
      setIsSuccess(true);
      toast({
        title: t("requestDemo.success.title"),
        description: t("requestDemo.success.description")
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem submitting your request. Please try again or contact us directly at info@offertio.dk"
      });
    } finally {
      setIsSubmitting(false);
    }
  }
  return <PageLayout hideHeader>
      <div className="bg-white">
        <div className="pt-24 pb-10">
          <div className="container mx-auto px-4 max-w-7xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-center text-purple-600">
              Kontakt os her
            </h1>
          </div>
        </div>
      
        <div className="container mx-auto max-w-6xl py-12 px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              {!isSuccess ? <div className="bg-white p-8 rounded-lg shadow">
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <FormField control={form.control} name="name" render={({
                    field
                  }) => <FormItem>
                            <FormLabel>{t("requestDemo.form.name.label")}</FormLabel>
                            <FormControl>
                              <Input placeholder={t("requestDemo.form.name.placeholder")} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                      
                      <FormField control={form.control} name="email" render={({
                    field
                  }) => <FormItem>
                            <FormLabel>{t("requestDemo.form.email.label")}</FormLabel>
                            <FormControl>
                              <Input placeholder={t("requestDemo.form.email.placeholder")} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                      
                      <FormField control={form.control} name="company" render={({
                    field
                  }) => <FormItem>
                            <FormLabel>{t("requestDemo.form.company.label")}</FormLabel>
                            <FormControl>
                              <Input placeholder={t("requestDemo.form.company.placeholder")} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                      
                      <FormField control={form.control} name="message" render={({
                    field
                  }) => <FormItem>
                            <FormLabel>{t("requestDemo.form.message.label")}</FormLabel>
                            <FormControl>
                              <Input placeholder={t("requestDemo.form.message.placeholder")} className="h-10" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                      
                      <Button type="submit" className="w-full" disabled={isSubmitting}>
                        {isSubmitting ? <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t("requestDemo.form.submit")}...
                          </> : t("requestDemo.form.submit")}
                      </Button>
                    </form>
                  </Form>
                </div> : <div className="bg-white p-12 rounded-lg shadow text-center">
                  <div className="flex justify-center mb-4">
                    <div className="bg-green-100 p-3 rounded-full">
                      <MailCheck className="h-12 w-12 text-green-600" />
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold mb-2">{t("requestDemo.success.title")}</h2>
                  <p className="text-gray-600">{t("requestDemo.success.description")}</p>
                </div>}
            </div>
            
            {/* Contact Information Card */}
            <div className="md:col-span-1">
              <div className="py-16 bg-gradient-to-r from-primary/90 via-purple-600/90 to-primary/90 text-white border-t">
                <h2 className="text-xl font-bold mb-6 text-center">Vi glæder os til at høre fra dig</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start">
                    <Mail className="h-5 w-5 text-primary mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium mb-1">E-mail:</p>
                      <a href="mailto:Info@offertio.dk" className="text-white/90 hover:text-white hover:glow-sm transition-all text-sm">Info@offertio.dk</a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Phone className="h-5 w-5 text-primary mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium mb-1">Telefon nr.:</p>
                      <a href="tel:+4528918872" className="text-white/90 hover:text-white hover:glow-sm transition-all text-sm">+45 28 91 88 72</a>
                    </div>
                  </div>
                  
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>;
};
export default RequestDemo;